import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { LogCard } from '../components/LogCard';
import { MarkdownView } from '../components/MarkdownView';
import { apiFetch } from '../api/client';
import { carUrl, eventUrl } from '../lib/slugId';
import { useAuth } from '../auth/AuthContext';
import type { Activity, Car, Event as EventModel, Paginated } from '../api/types';
import { CarCardMedia } from '../components/CarCardMedia';

export function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [cars, setCars] = useState<Car[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [recentCars, setRecentCars] = useState<Car[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<EventModel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const nowIso = new Date().toISOString();
    Promise.all([
      apiFetch<Paginated<Car>>(`/api/v1/cars?filter[user_id]=${user.id}&per_page=20`),
      apiFetch<Paginated<Activity>>(`/api/v1/activities?filter[user_id]=${user.id}&include=car&sort=-scheduled_at&per_page=20`),
      apiFetch<Paginated<Car>>(`/api/v1/cars?sort=-created_at&per_page=4&include=user`),
      apiFetch<Paginated<EventModel>>(`/api/v1/events?filter[starts_after]=${encodeURIComponent(nowIso)}&sort=starts_at&include=organizer&per_page=4`),
    ])
      .then(([carsRes, actsRes, recentRes, eventsRes]) => {
        setCars(carsRes.data);
        setActivities(actsRes.data);
        setRecentCars(recentRes.data);
        setUpcomingEvents(eventsRes.data);
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader />
      <Stack spacing={4}>
      {/* My garage */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Typography variant="h4">{t('dashboard.myGarage')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/cars/new"
        >
          {t('common.new')}
        </Button>
      </Stack>

      {cars.length === 0 ? (
        <Typography color="text.secondary">{t('dashboard.noCars')}</Typography>
      ) : (
        <Grid container spacing={3}>
          {cars.map((car) => (
            <Grid key={car.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardActionArea component={RouterLink} to={carUrl(car)}>
                  <CarCardMedia car={car} height={180} />
                  <CardContent>
                    <Typography variant="h6">{car.display_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {car.year} {car.make} {car.model}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Divider />

      {/* My activity */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Typography variant="h4">{t('dashboard.myActivity')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/activities/new"
        >
          {t('common.new')}
        </Button>
      </Stack>

      {activities.length === 0 ? (
        <Typography color="text.secondary">{t('dashboard.noActivity')}</Typography>
      ) : (
        <Stack spacing={2}>
          {activities.map((act) => (
            <LogCard
              key={act.id}
              date={act.scheduled_at ?? act.created_at}
              title={act.car?.display_name ?? ''}
              chip={t(`activityType.${act.type}`, { defaultValue: act.type_label })}
              description={<MarkdownView>{act.summary}</MarkdownView>}
            />
          ))}
        </Stack>
      )}

      <Divider />

      {/* Recently added + Upcoming events side by side */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              <Typography variant="h5">{t('dashboard_sections.recentlyAdded')}</Typography>
              <Button component={RouterLink} to="/explore" size="small">
                {t('dashboard_sections.viewAll')}
              </Button>
            </Stack>
            <Grid container spacing={2}>
              {recentCars.map((car) => (
                <Grid key={car.id} size={{ xs: 12, sm: 6 }}>
                  <Card>
                    <CardActionArea component={RouterLink} to={carUrl(car)}>
                      <CarCardMedia car={car} height={160} />
                      <CardContent>
                        <Typography variant="h6">{car.display_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {car.year} {car.make} {car.model}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              <Typography variant="h5">{t('dashboard_sections.upcomingEvents')}</Typography>
              <Button component={RouterLink} to="/events" size="small">
                {t('dashboard_sections.viewAll')}
              </Button>
            </Stack>
            {upcomingEvents.length === 0 ? (
              <Typography color="text.secondary">{t('dashboard_sections.noUpcoming')}</Typography>
            ) : (
              <Stack spacing={2}>
                {upcomingEvents.map((evt) => (
                  <LogCard
                    key={evt.id}
                    date={evt.starts_at}
                    title={evt.title}
                    location={`${evt.location_name}${evt.city ? ', ' + evt.city : ''}`}
                    to={eventUrl(evt)}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Grid>
      </Grid>
      </Stack>
    </Container>
  );
}
